import { UserRoles } from 'types';
import { allRoles } from 'appConstants';
import { IconName } from 'components/Icon/Icon';

export interface ISubRoute {
	path: string;
	name: string;
	permissions: UserRoles[];
}

export interface IRoute {
	path: string;
	name: string;
	permissions: UserRoles[];
	subroutes: ISubRoute[];
}

const routes: IRoute[] = [
	{
		path: '/clients',
		name: 'customersAndRestaurants',
		permissions: allRoles,
		subroutes: [
			{
				path: '/clients/customers',
				name: 'customers',
				permissions: [
					UserRoles.SUPER_USER,
					UserRoles.CLIENT_MANAGER,
					UserRoles.GROUP_MANAGER,
					UserRoles.TECHNICAL,
				],
			},
			{
				path: '/clients/customers-new-ui',
				name: 'customersNewUi',
				permissions: process.env.HAS_CUSTOMERS_NEW_UI
					? [UserRoles.SUPER_USER, UserRoles.CLIENT_MANAGER, UserRoles.GROUP_MANAGER, UserRoles.TECHNICAL]
					: [],
			},
			{
				path: '/clients/restaurants',
				name: 'restaurants',
				permissions: allRoles,
			},
		],
	},
	{
		path: '/menu',
		name: 'menu',
		permissions: allRoles,
		subroutes: [
			{
				path: '/menu/category',
				name: 'category',
				permissions: allRoles,
			},
			{
				path: '/menu/subcategory',
				name: 'subcategory',
				permissions: allRoles,
			},
			{
				path: '/menu/dishes',
				name: 'dishes',
				permissions: allRoles,
			},
			{
				path: '/menu/dishes-assignment',
				name: 'dishAssignment',
				permissions: allRoles,
			},
			{
				path: '/menu/modifier-groups',
				name: 'modifierGroups',
				permissions: allRoles,
			},
			{
				path: '/menu/modifiers',
				name: 'modifiers',
				permissions: allRoles,
			},
			{
				path: '/menu/combo-group',
				name: 'comboGroup',
				permissions: allRoles,
			},
		],
	},
	{
		path: '/menu-v2',
		name: 'menu-v2',
		permissions: process.env.HAS_MENU_V2
			? [
					UserRoles.SUPER_USER,
					UserRoles.CLIENT_MANAGER,
					UserRoles.GROUP_MANAGER,
					UserRoles.RESTAURANT_MANAGER,
					UserRoles.TECHNICAL,
					UserRoles.WAITER,
					UserRoles.RKL_SUPPORT_MANAGER,
			  ]
			: [],
		subroutes: [
			{
				path: '/menu-v2/category',
				name: 'category-v2',
				permissions: process.env.HAS_MENU_V2
					? [
							UserRoles.SUPER_USER,
							UserRoles.CLIENT_MANAGER,
							UserRoles.GROUP_MANAGER,
							UserRoles.RESTAURANT_MANAGER,
							UserRoles.TECHNICAL,
							UserRoles.WAITER,
							UserRoles.RKL_SUPPORT_MANAGER,
					  ]
					: [],
			},
			{
				path: '/menu-v2/subcategory',
				name: 'subcategory-v2',
				permissions: process.env.HAS_MENU_V2
					? [
							UserRoles.SUPER_USER,
							UserRoles.CLIENT_MANAGER,
							UserRoles.GROUP_MANAGER,
							UserRoles.RESTAURANT_MANAGER,
							UserRoles.TECHNICAL,
							UserRoles.WAITER,
							UserRoles.RKL_SUPPORT_MANAGER,
					  ]
					: [],
			},
			{
				path: '/menu-v2/dishes',
				name: 'dishes-v2',
				permissions: process.env.HAS_MENU_V2
					? [
							UserRoles.SUPER_USER,
							UserRoles.CLIENT_MANAGER,
							UserRoles.GROUP_MANAGER,
							UserRoles.RESTAURANT_MANAGER,
							UserRoles.TECHNICAL,
							UserRoles.WAITER,
							UserRoles.RKL_SUPPORT_MANAGER,
					  ]
					: [],
			},
			...(process.env.HAS_DISH_ASSIGNMENT_MENU_V2
				? [
						{
							path: '/menu-v2/dishes-assignment',
							name: 'dishAssignment',
							permissions: [
								UserRoles.SUPER_USER,
								UserRoles.CLIENT_MANAGER,
								UserRoles.GROUP_MANAGER,
								UserRoles.RESTAURANT_MANAGER,
								UserRoles.TECHNICAL,
								UserRoles.WAITER,
								UserRoles.RKL_SUPPORT_MANAGER,
							],
						},
				  ]
				: []),
			{
				path: '/menu-v2/modifier-groups',
				name: 'modifierGroups-v2',
				permissions: process.env.HAS_MENU_V2
					? [
							UserRoles.SUPER_USER,
							UserRoles.CLIENT_MANAGER,
							UserRoles.GROUP_MANAGER,
							UserRoles.RESTAURANT_MANAGER,
							UserRoles.TECHNICAL,
							UserRoles.WAITER,
							UserRoles.RKL_SUPPORT_MANAGER,
					  ]
					: [],
			},
			{
				path: '/menu-v2/modifiers',
				name: 'modifiers-v2',
				permissions: process.env.HAS_MENU_V2
					? [
							UserRoles.SUPER_USER,
							UserRoles.CLIENT_MANAGER,
							UserRoles.GROUP_MANAGER,
							UserRoles.RESTAURANT_MANAGER,
							UserRoles.TECHNICAL,
							UserRoles.WAITER,
							UserRoles.RKL_SUPPORT_MANAGER,
					  ]
					: [],
			},
		],
	},
	{
		path: '/teasers',
		name: 'teasers',
		permissions: allRoles,
		subroutes: [],
	},
	{
		path: '/entertainment',
		name: 'entertainment',
		permissions: [UserRoles.SUPER_USER, UserRoles.TECHNICAL],
		subroutes: [
			{
				path: '/entertainment/games',
				name: 'games',
				permissions: [UserRoles.SUPER_USER, UserRoles.TECHNICAL],
			},
			{
				path: '/entertainment/videos',
				name: 'videos',
				permissions: [UserRoles.SUPER_USER, UserRoles.TECHNICAL],
			},
		],
	},
	{
		path: '/users',
		name: 'users',
		permissions: [UserRoles.SUPER_USER, UserRoles.TECHNICAL],
		subroutes: [],
	},
	{
		path: '/devices',
		name: 'devices',
		permissions: [UserRoles.SUPER_USER, UserRoles.TECHNICAL],
		subroutes: [],
	},
	{
		path: '/watches',
		name: 'watches',
		permissions: process.env.HAS_WATCHES ? [UserRoles.SUPER_USER, UserRoles.TECHNICAL] : [],
		subroutes: [],
	},
	{
		path: '/app',
		name: 'firmwareAndApp',
		permissions: [UserRoles.SUPER_USER, UserRoles.TECHNICAL],
		subroutes: [
			{
				path: '/app/firmware',
				name: 'firmware',
				permissions: [UserRoles.SUPER_USER, UserRoles.TECHNICAL],
			},
			{
				path: '/app/tablet-app',
				name: 'tabletApp',
				permissions: [UserRoles.SUPER_USER, UserRoles.TECHNICAL],
			},
		],
	},
	{
		path: '/credentials',
		name: 'credentials',
		permissions: [UserRoles.SUPER_USER],
		subroutes: [],
	},
	{
		path: '/adminSettings',
		name: 'adminSettings',
		permissions: [UserRoles.SUPER_USER, UserRoles.TECHNICAL],
		subroutes: [],
	},
];

export default routes;

export const routeIconNames: Record<string, IconName> = {
	customersAndRestaurants: IconName.customersAndRestaurants,
	menu: IconName.menu,
	'menu-v2': IconName.menuV2,
	teasers: IconName.teasers,
	entertainment: IconName.entertainment,
	users: IconName.users,
	devices: IconName.devices,
	watches: IconName.watches,
	firmwareAndApp: IconName.firmwareAndApp,
	credentials: IconName.credentials,
	adminSettings: IconName.adminSettings,
};

export const routesPathsToNames = new Map([
	['/clients', 'customersAndRestaurants'],
	['/clients/customers', 'customers'],
	['/clients/customers-new-ui', 'customersNewUi'],
	['/clients/customers-new-ui/create', 'createCustomer'],
	['/clients/customers-new-ui/edit', 'editCustomer'],
	['/clients/customers/create', 'createCustomer'],
	['/clients/customers/edit', 'editCustomer'],
	['/clients/restaurants', 'restaurants'],
	['/clients/restaurants/create', 'createRestaurant'],
	['/clients/restaurants/edit', 'editRestaurant'],
	['/menu', 'menu'],
	['/menu/category', 'category'],
	['/menu/category/create', 'createCategory'],
	['/menu/category/edit', 'editCategory'],
	['/menu/category/positions', 'changePosition'],
	['/menu/subcategory', 'subcategory'],
	['/menu/subcategory/create', 'createSubcategory'],
	['/menu/subcategory/edit', 'editSubcategory'],
	['/menu/subcategory/positions', 'changePosition'],
	['/menu/dishes', 'dishes'],
	['/menu/dishes/create', 'createDish'],
	['/menu/dishes/edit', 'editDish'],
	['/menu/dishes/positions', 'changePosition'],
	['/menu/dishes-assignment', 'dishAssignment'],
	['/menu/modifier-groups', 'modifierGroups'],
	['/menu/modifier-groups/create', 'createModifierGroups'],
	['/menu/modifier-groups/edit', 'editModifierGroups'],
	['/menu/modifiers', 'modifiers'],
	['/menu/modifiers/create', 'createModifier'],
	['/menu/modifiers/edit', 'editModifier'],
	['/menu/modifiers/positions', 'changePosition'],
	['/menu/combo-group', 'comboGroup'],
	['/menu/combo-group/create', 'createComboGroup'],
	['/menu/combo-group/edit', 'editComboGroup'],
	['/menu/combo-group/positions', 'changePosition'],
	['/menu-v2', 'menu-v2'],
	['/menu-v2/category', 'category-v2'],
	['/menu-v2/category/create', 'createCategory'],
	['/menu-v2/category/edit', 'editCategory'],
	['/menu-v2/category/positions', 'changePosition'],
	['/menu-v2/category/edit-restaurant-properties', 'editRestaurantProperties'],
	['/menu-v2/category/view', 'viewCategory'],
	['/menu-v2/subcategory', 'subcategory-v2'],
	['/menu-v2/subcategory/create', 'createSubcategory'],
	['/menu-v2/subcategory/edit', 'editSubcategory'],
	['/menu-v2/subcategory/positions', 'changePosition'],
	['/menu-v2/subcategory/edit-restaurant-properties', 'editRestaurantProperties'],
	['/menu-v2/subcategory/view', 'viewSubcategory'],
	['/menu-v2/dishes', 'dishes-v2'],
	['/menu-v2/dishes/create', 'createDish'],
	['/menu-v2/dishes/edit', 'editDish'],
	['/menu-v2/dishes/positions', 'changePosition'],
	['/menu-v2/dishes/portion-positions', 'changePortionPosition'],
	['/menu-v2/dishes/edit-restaurant-properties', 'editRestaurantProperties'],
	['/menu-v2/dishes/view', 'viewDish'],
	['/menu-v2/dishes-assignment', 'dishAssignment'],
	['/menu-v2/modifier-groups', 'modifierGroups-v2'],
	['/menu-v2/modifier-groups/create', 'createModifierGroups'],
	['/menu-v2/modifier-groups/edit', 'editModifierGroups'],
	['/menu-v2/modifier-groups/view', 'viewModifierGroups'],
	['/menu-v2/modifier-groups/edit-restaurant-properties', 'editRestaurantProperties'],
	['/menu-v2/modifiers', 'modifiers-v2'],
	['/menu-v2/modifiers/create', 'createModifier'],
	['/menu-v2/modifiers/edit', 'editModifier'],
	['/menu-v2/modifiers/view', 'viewModifier'],
	['/menu-v2/modifiers/edit-restaurant-properties', 'editRestaurantProperties'],
	['/teasers', 'teasers'],
	['/teasers/create', 'createTeaser'],
	['/teasers/edit', 'editTeaser'],
	['/entertainment', 'entertainment'],
	['/entertainment/games', 'games'],
	['/entertainment/games/create', 'createGame'],
	['/entertainment/games/edit', 'editGame'],
	['/entertainment/games/developers', 'gameDevelopers'],
	['/entertainment/games/categories', 'gameCategories'],
	['/entertainment/games/categories/create', 'createGameCategory'],
	['/entertainment/games/categories/edit', 'editGameCategory'],
	['/entertainment/games/games-assign', 'gameAssignment'],
	['/entertainment/games/positions', 'changePosition'],
	['/entertainment/videos', 'videos'],
	['/entertainment/videos/create', 'createVideo'],
	['/entertainment/videos/edit', 'editVideo'],
	['/entertainment/videos/categories', 'videoCategories'],
	['/entertainment/videos/categories/create', 'createVideoCategory'],
	['/entertainment/videos/categories/edit', 'editVideoCategory'],
	['/entertainment/videos/studios', 'videoStudios'],
	['/entertainment/videos/videos-assign', 'videosAssignment'],
	['/users', 'users'],
	['/users/create', 'createUser'],
	['/users/edit', 'editUser'],
	['/users/me', 'infoUser'],
	['/devices', 'devices'],
	['/devices/create', 'createDevice'],
	['/devices/edit', 'editDevice'],
	['/watches', 'watches'],
	['/watches/create', 'createWatch'],
	['/watches/edit', 'editWatch'],
	['/app', 'firmwareAndApp'],
	['/app/firmware', 'firmware'],
	['/app/firmware/create', 'createFirmware'],
	['/app/firmware/edit', 'editFirmware'],
	['/app/firmware/assignment', 'firmwareAssignment'],
	['/app/tablet-app', 'tabletApp'],
	['/app/tablet-app/create', 'createTabletApp'],
	['/app/tablet-app/edit', 'editTabletApp'],
	['/app/tablet-app/assignment', 'tabletAppAssignment'],
	['/credentials', 'credentials'],
	['/credentials/create', 'createCredentials'],
	['/credentials/edit', 'editCredentials'],
	['/adminSettings', 'adminSettings'],
	['/adminSettings/add', 'addWording'],
]);
